import { useState, useEffect } from "react";

const APIS = {
  SIT:  ["https://bn360.bluenett.solutions/deploy-api"],
  PROD: ["https://bn360.bluenett.solutions/prod11-deploy-api", "https://bn360.bluenett.solutions/prod17-deploy-api"],
};
const GES_API = "https://bn360.bluenett.solutions/ges-deploy-api";

export default function DeployPanel({ theme, role, user }) {
  const SCREEN_PASSWORD = "Deploye@123";
  const [unlocked, setUnlocked] = useState(false);
  const [gatePw, setGatePw] = useState("");
  const [gateErr, setGateErr] = useState("");
  const [envMode, setEnvMode] = useState("SIT");     // SIT | PROD
  const [containers, setContainers] = useState([]);  // merged, with servers[] per name
  const [selected, setSelected] = useState("");
  const [build, setBuild] = useState("");
  const [password, setPassword] = useState("");
  const [preview, setPreview] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployStep, setDeployStep] = useState(0);
  const [result, setResult] = useState(null);        // {perServer:[{server,...}]}
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState([]);

  const bg = theme?.bg || "#111827";
  const card = theme?.bgCard || "#1f2937";
  const border = theme?.border || "#374151";
  const txt = theme?.text || "#f1f5f9";
  const muted = theme?.textMuted || "#94a3b8";
  const accent = theme?.accent || "#0ea5e9";
  const mono = "'SF Mono',Monaco,Consolas,monospace";
  const isAdmin = role === "admin" || role === "superadmin";

  // load + merge containers across the env's servers (by container name)
  const loadContainers = async () => {
    setLoading(true);
    if (envMode === "GES") {
      // GES is a single compose unit (ges-server + ges-client)
      try {
        const r = await fetch(`${GES_API}/health`);
        const d = await r.json();
        const cb = (d.current_build || "sit-?").replace("sit-", "");
        setContainers([{ name: "ges-server + ges-client", current_build: cb, image: "bhl-ges compose",
          servers: [{ api: GES_API, server: "BHL-GES (bn-intapp)", ip: "10.0.1.37", build: cb, isGes: true }] }]);
      } catch (e) { setContainers([]); }
      setLoading(false);
      return;
    }
    const apis = APIS[envMode];
    const map = {};
    // parallel fetch (fast) instead of sequential
    const results = await Promise.all(apis.map(api =>
      fetch(`${api}/containers`).then(r=>r.json()).then(d=>({api,d})).catch(()=>({api,d:{containers:[]}}))
    ));
    results.forEach(({api,d}) => {
      (d.containers || []).forEach(c => {
        if (!map[c.name]) map[c.name] = { name:c.name, current_build:c.current_build, image:c.image, servers:[] };
        map[c.name].servers.push({ api, server:c.server, ip:c.server_ip, build:c.current_build });
      });
    });
    setContainers(Object.values(map).sort((a,b)=>a.name.localeCompare(b.name)));
    setLoading(false);
  };

  const loadHistory = async () => {
    if (envMode === "GES") {
      try { const r = await fetch(`${GES_API}/log?limit=6`); const d = await r.json();
        setHistory((d.log||[]).map(h=>({container:"ges-server+client", build:h.to, old_image:h.from?("x-"+h.from.replace("sit-","")):null, new_image:"x-"+(h.to||"").replace("sit-",""), user:h.user, result:h.result, time:h.time, server:"BHL-GES"}))); } catch(e){ setHistory([]); }
      return;
    }
    const apis = APIS[envMode];
    const results = await Promise.all(apis.map(api =>
      fetch(`${api}/log?limit=5`).then(r=>r.json()).then(d=>d.log||[]).catch(()=>[])
    ));
    const all = [].concat(...results);
    all.sort((a,b)=>(b.time||"").localeCompare(a.time||""));
    setHistory(all.slice(0,6));
  };

  useEffect(() => { setSelected(""); setPreview(null); setResult(null); setBuild(""); loadContainers(); loadHistory(); /* eslint-disable-next-line */ }, [envMode]);

  const selectedC = containers.find(c => c.name === selected);

  const doPreview = async () => {
    setPreview(null); setResult(null);
    if (!selectedC) return;
    try {
      const r = await fetch(`${selectedC.servers[0].api}/preview`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ container:selected, build }),
      });
      const d = await r.json();
      if (d.detail) setResult({ result:"ERROR", msg:d.detail });
      else setPreview(d);
    } catch(e){ setResult({ result:"ERROR", msg:"Preview failed" }); }
  };

  const DEPLOY_STEPS = [
    { icon:"🔍", label:"Checking container config" },
    { icon:"⬇️", label:"Pulling docker image" },
    { icon:"⏹️", label:"Stopping old container" },
    { icon:"🗑️", label:"Removing old container" },
    { icon:"▶️", label:"Starting new container" },
    { icon:"✓", label:"Verifying health (30s)" },
    { icon:"📧", label:"Sending notification" },
  ];

  const doDeploy = async () => {
    if (!password) { setResult({ result:"ERROR", msg:"Password required" }); return; }
    setDeploying(true); setResult(null); setDeployStep(0);
    // animate steps while deploy runs in background
    const stepTimers = [];
    const timings = [500, 3000, 8000, 11000, 14000, 20000, 42000]; // ms when each step activates
    timings.forEach((t, i) => { stepTimers.push(setTimeout(()=>setDeployStep(i), t)); });
    const clearTimers = () => stepTimers.forEach(clearTimeout);
    const perServer = [];
    // GES = single compose deploy
    if (selectedC.servers[0] && selectedC.servers[0].isGes) {
      try {
        const r = await fetch(`${GES_API}/deploy`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ build, user:user||"unknown", password }),
        });
        const d = await r.json();
        clearTimers();
        setResult({ result: d.result==="SUCCESS"?"SUCCESS":"FAILED",
          perServer:[{ server:"BHL-GES (bn-intapp)", ok:d.result==="SUCCESS", new_image:"ges:sit-"+build, steps:d.steps, msg:d.reason }] });
        setDeploying(false); setPassword(""); loadContainers(); loadHistory();
      } catch(e){ clearTimers(); setResult({result:"ERROR", msg:"GES deploy failed"}); setDeploying(false); }
      return;
    }
    // deploy to ALL servers this container runs on (sync)
    for (const srv of selectedC.servers) {
      try {
        const r = await fetch(`${srv.api}/deploy`, {
          method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({ container:selected, build, user:user||"unknown", password }),
        });
        const d = await r.json();
        perServer.push({ server:srv.server, ok:d.result==="SUCCESS", ...d });
      } catch(e){ perServer.push({ server:srv.server, ok:false, result:"ERROR", msg:"unreachable" }); }
    }
    const allOk = perServer.every(p=>p.ok);
    clearTimers();
    setResult({ result: allOk?"SUCCESS":"PARTIAL", perServer });
    setDeploying(false); setPassword("");
    loadContainers(); loadHistory();
  };

  const reset = () => { setPreview(null); setResult(null); setBuild(""); setPassword(""); };
  const filtered = containers.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  if (!isAdmin) {
    return (
      <div style={{flex:1, background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',sans-serif"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:64,height:64,borderRadius:16,background:`${accent}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,margin:"0 auto 16px"}}>🔒</div>
          <div style={{fontSize:17,color:txt,fontWeight:700}}>Admin Access Required</div>
          <div style={{fontSize:13,marginTop:6,color:muted}}>Deployment is restricted to administrators.</div>
        </div>
      </div>
    );
  }

  const stepLabel = { pull:"Pulling image", stop:"Stopping old", remove:"Removing old", rmi_old:"Cleaning old image", run:"Starting new", verify:"Verifying" };

  // password gate - must unlock before seeing deploy screen
  if (!unlocked) {
    const tryUnlock = () => {
      if (gatePw === SCREEN_PASSWORD) { setUnlocked(true); setGateErr(""); }
      else { setGateErr("Incorrect password"); setGatePw(""); }
    };
    return (
      <div style={{flex:1, background:bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',sans-serif", padding:20}}>
        <div style={{width:340, background:card, border:`1px solid ${border}`, borderRadius:16, padding:"32px 28px", textAlign:"center"}}>
          <div style={{width:60, height:60, borderRadius:15, background:`linear-gradient(135deg,${accent},#06b6d4)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 16px"}}>🔐</div>
          <div style={{fontSize:18, fontWeight:800, color:txt, marginBottom:6}}>Docker Deploy — Protected</div>
          <div style={{fontSize:13, color:muted, marginBottom:22}}>Enter the deploy access password to continue</div>
          <input type="password" value={gatePw} autoFocus
            onChange={e=>{setGatePw(e.target.value);setGateErr("");}}
            onKeyDown={e=>e.key==="Enter"&&tryUnlock()}
            placeholder="Access password"
            style={{width:"100%", boxSizing:"border-box", padding:"12px 15px", marginBottom:12, borderRadius:10, border:`1px solid ${gateErr?"#ef4444":border}`, background:bg, color:txt, fontSize:14, outline:"none", textAlign:"center"}}/>
          {gateErr && <div style={{fontSize:12, color:"#ef4444", marginBottom:12}}>⚠ {gateErr}</div>}
          <button onClick={tryUnlock} style={{width:"100%", padding:"12px", borderRadius:10, border:"none", background:`linear-gradient(135deg,${accent},#06b6d4)`, color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer"}}>Unlock →</button>
        </div>
      </div>
    );
  }


  return (
    <div style={{flex:1, overflowY:"auto", background:bg, padding:"28px 32px", fontFamily:"'Inter',sans-serif"}}>
      {/* Header + env toggle */}
      <div className="dep-header" style={{display:"flex", alignItems:"center", gap:14, marginBottom:24, flexWrap:"wrap"}}>
        <div style={{width:44,height:44,borderRadius:12,background:`linear-gradient(135deg,${accent},#06b6d4)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:`0 4px 14px ${accent}44`}}>🚀</div>
        <div>
          <div style={{fontSize:21,fontWeight:800,color:txt,letterSpacing:"-0.02em"}}>Docker Deploy</div>
          <div style={{fontSize:12.5,color:muted}}>Select environment, container, and build number</div>
        </div>
        {/* SIT / PROD toggle */}
        <div style={{marginLeft:"auto", display:"flex", background:card, border:`1px solid ${border}`, borderRadius:12, padding:4, gap:4}}>
          {["SIT","PROD","GES"].map(m=>(
            <button key={m} onClick={()=>setEnvMode(m)} style={{
              padding:"8px 20px", borderRadius:9, border:"none", cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:mono,
              background: envMode===m ? (m==="PROD"?"#ef4444":m==="GES"?"#fb7185":"#10b981") : "transparent",
              color: envMode===m ? "#fff" : muted, transition:"all .15s" }}>
              {m}{m==="PROD"?" (2)":m==="GES"?" (compose)":""}
            </button>
          ))}
        </div>
      </div>

      {envMode==="PROD" && (
        <div style={{fontSize:12.5, color:"#c2410c", background:"#fff7ed", border:"1px solid #fdba74", borderRadius:10, padding:"11px 15px", marginBottom:18, display:"flex", gap:9, fontWeight:500, boxShadow:"0 2px 6px rgba(234,88,12,0.08)"}}>
          <span style={{fontSize:15}}>⚠️</span><span><b>PROD has 2 load-balanced servers</b> (.11 + .17). Containers running on both will be deployed to both automatically, in sync.</span>
        </div>
      )}

      <div className="dep-grid" style={{display:"grid", gridTemplateColumns:"1.1fr 1fr", gap:22, alignItems:"start"}}>
        {/* LEFT - select */}
        <div style={{background:card, border:`1px solid ${border}`, borderRadius:14, padding:20}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
            <span style={{width:22,height:22,borderRadius:6,background:`${accent}22`,color:accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>1</span>
            <span style={{fontSize:14,fontWeight:700,color:txt}}>Select Container</span>
          </div>
          <div style={{position:"relative",marginBottom:12}}>
            <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:muted,fontSize:14}}>🔍</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
              style={{width:"100%",boxSizing:"border-box",padding:"10px 12px 10px 34px",borderRadius:9,border:`1px solid ${border}`,background:bg,color:txt,fontSize:13,outline:"none"}}/>
          </div>
          <div style={{maxHeight:340,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
            {loading && <div style={{padding:20,color:muted,fontSize:13,textAlign:"center"}}>Loading…</div>}
            {!loading && filtered.length===0 && <div style={{padding:20,color:muted,fontSize:13,textAlign:"center"}}>No containers.</div>}
            {filtered.map((c,i)=>{
              const on = selected===c.name;
              const both = c.servers.length>1;
              return (
                <div key={i} onClick={()=>{setSelected(c.name);reset();}}
                  style={{padding:"11px 13px",cursor:"pointer",borderRadius:9,transition:"all .12s",
                    background:on?`${accent}1a`:bg,border:`1px solid ${on?accent:"transparent"}`,
                    display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:13,fontFamily:mono,color:on?accent:txt,fontWeight:on?600:400}}>
                    {c.name} {both && <span title="runs on both servers" style={{color:"#f59e0b"}}>🔗</span>}
                  </span>
                  <span style={{fontSize:10.5,fontFamily:mono,color:muted,background:on?`${accent}22`:card,padding:"2px 8px",borderRadius:5}}>#{c.current_build}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT */}
        <div style={{display:"flex",flexDirection:"column",gap:18}}>
          <div style={{background:card,border:`1px solid ${border}`,borderRadius:14,padding:20}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
              <span style={{width:22,height:22,borderRadius:6,background:`${accent}22`,color:accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>2</span>
              <span style={{fontSize:14,fontWeight:700,color:txt}}>New Build Number</span>
            </div>
            {!selected && <div style={{fontSize:12.5,color:muted,padding:"8px 0"}}>← Select a container first</div>}
            {selectedC && (
              <>
                <div style={{fontSize:12,color:muted,marginBottom:8,fontFamily:mono}}>
                  {selected} · currently <span style={{color:"#f59e0b"}}>#{selectedC.current_build}</span>
                </div>
                {selectedC.servers.length>1 && (
                  <div style={{fontSize:11.5,color:"#0ea5e9",background:"#0ea5e918",borderRadius:8,padding:"8px 12px",marginBottom:10}}>
                    🔗 Runs on {selectedC.servers.length} servers: {selectedC.servers.map(s=>s.server).join(" + ")} — will deploy to <b>all</b>
                  </div>
                )}
                <div style={{display:"flex",gap:10}}>
                  <input value={build} onChange={e=>setBuild(e.target.value)} placeholder="e.g. 858"
                    style={{flex:1,boxSizing:"border-box",padding:"11px 14px",borderRadius:9,border:`1px solid ${border}`,background:bg,color:txt,fontSize:15,fontFamily:mono,outline:"none"}}/>
                  <button onClick={doPreview} disabled={!build}
                    style={{padding:"0 20px",borderRadius:9,border:`1px solid ${accent}`,background:"transparent",color:accent,fontSize:13,fontWeight:600,cursor:build?"pointer":"not-allowed",whiteSpace:"nowrap"}}>Preview →</button>
                </div>
              </>
            )}
          </div>

          {preview && (
            <div style={{background:card,border:`1px solid ${border}`,borderRadius:14,padding:20}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                <span style={{width:22,height:22,borderRadius:6,background:`${accent}22`,color:accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700}}>3</span>
                <span style={{fontSize:14,fontWeight:700,color:txt}}>Review &amp; Deploy</span>
              </div>
              <div style={{background:bg,borderRadius:10,padding:15,marginBottom:14,fontFamily:mono,fontSize:12.5,display:"flex",flexDirection:"column",gap:9}}>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:muted}}>version</span><span><span style={{color:"#f59e0b"}}>#{preview.old_image.split("-").pop()}</span> → <span style={{color:"#10b981",fontWeight:600}}>#{preview.new_image.split("-").pop()}</span></span></div>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:muted}}>ports</span><span style={{color:txt}}>{(preview.ports||[]).join(", ")||"—"}</span></div>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:muted}}>config</span><span style={{color:"#10b981"}}>{preview.env_count} env · auto</span></div>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:muted}}>target</span><span style={{color:accent}}>{selectedC.servers.map(s=>s.server).join(" + ")}</span></div>
              </div>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="🔒 Admin password" disabled={deploying}
                style={{width:"100%",boxSizing:"border-box",padding:"11px 14px",marginBottom:12,borderRadius:9,border:`1px solid ${border}`,background:bg,color:txt,fontSize:13,outline:"none"}}/>
              <button onClick={doDeploy} disabled={deploying||!password}
                style={{width:"100%",padding:"13px",borderRadius:10,border:"none",
                  background:deploying?bg:(password?`linear-gradient(135deg,${accent},#06b6d4)`:border),
                  color:deploying?accent:"#fff",fontSize:14.5,fontWeight:700,cursor:deploying||!password?"not-allowed":"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
                {deploying ? (<><span className="dep-spin"/>Deploying…</>) : `🚀 Deploy${selectedC.servers.length>1?" to Both":""}`}
              </button>

              {/* animated deploy steps */}
              {deploying && (
                <div style={{marginTop:16, background:bg, borderRadius:10, padding:"14px 16px"}}>
                  {DEPLOY_STEPS.map((st,i)=>{
                    const active = i===deployStep;
                    const done = i<deployStep;
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:11,padding:"6px 0",
                        opacity: (active||done)?1:0.35, transition:"opacity .3s"}}>
                        <span style={{width:22,height:22,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,
                          background: done?"#10b98122":(active?`${accent}22`:"transparent"),
                          border:`1.5px solid ${done?"#10b981":(active?accent:border)}`,color:done?"#10b981":(active?accent:muted)}}>
                          {done?"✓":(active?<span className="dep-spin" style={{width:11,height:11,borderTopColor:accent}}/>:st.icon)}
                        </span>
                        <span style={{fontSize:12.5,fontFamily:mono,color:done?"#10b981":(active?txt:muted),fontWeight:active?600:400}}>
                          {st.label}{active?"…":""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {result && (
            <div style={{background:card,border:`1px solid ${result.result==="SUCCESS"?"#10b981":result.result==="PARTIAL"?"#f59e0b":"#ef4444"}`,borderRadius:14,padding:20}}>
              <div style={{fontSize:14.5,fontWeight:700,marginBottom:result.perServer?14:0,color:result.result==="SUCCESS"?"#10b981":result.result==="PARTIAL"?"#f59e0b":"#ef4444"}}>
                {result.result==="SUCCESS"?"✅ Deployed Successfully":result.result==="PARTIAL"?"⚠️ Partial — check servers":"❌ "+(result.msg||"Failed")}
              </div>
              {result.perServer && result.perServer.map((p,i)=>(
                <div key={i} style={{marginBottom:10,paddingBottom:10,borderBottom:i<result.perServer.length-1?`1px solid ${border}`:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,fontFamily:mono,marginBottom:6}}>
                    <span style={{color:p.ok?"#10b981":"#ef4444"}}>{p.ok?"✓":"✗"}</span>
                    <span style={{color:txt,fontWeight:600}}>{p.server}</span>
                    {p.new_image && <span style={{color:muted,fontSize:11}}>→ #{p.new_image.split("-").pop()}</span>}
                    {p.msg && <span style={{color:"#ef4444",fontSize:11}}>{p.msg}</span>}
                  </div>
                  {p.steps && (
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",paddingLeft:18}}>
                      {p.steps.map((s,j)=>(<span key={j} style={{fontSize:10.5,fontFamily:mono,color:s.ok?"#10b981":"#ef4444"}}>{s.ok?"✓":"✗"}{stepLabel[s.step]||s.step}</span>))}
                    </div>
                  )}
                </div>
              ))}
              <button onClick={reset} style={{marginTop:8,width:"100%",padding:"9px",borderRadius:8,border:`1px solid ${border}`,background:bg,color:txt,fontSize:12.5,cursor:"pointer"}}>Deploy another</button>
            </div>
          )}
        </div>
      </div>

      {/* history */}
      <div style={{marginTop:24,background:card,border:`1px solid ${border}`,borderRadius:14,padding:20}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <span style={{fontSize:15}}>🕑</span>
          <span style={{fontSize:14,fontWeight:700,color:txt}}>Recent Deployments — {envMode}</span>
          <span style={{fontSize:11,color:muted,marginLeft:"auto"}}>last 6</span>
        </div>
        {history.length===0 && <div style={{fontSize:12.5,color:muted,padding:"6px 0"}}>No deployments yet.</div>}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {history.map((h,i)=>{
            const fromB = h.old_image ? h.old_image.split("-").pop() : null;
            const toB = h.new_image ? h.new_image.split("-").pop() : h.build;
            const ok = h.result==="SUCCESS";
            return (
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",background:bg,borderRadius:9,fontSize:12.5,flexWrap:"wrap",
              borderLeft:`3px solid ${ok?"#10b981":"#ef4444"}`}}>
              <span style={{fontSize:14}}>{ok?"✅":"❌"}</span>
              <span style={{fontFamily:mono,color:txt,fontWeight:700,minWidth:130}}>{h.container}</span>
              <span style={{fontFamily:mono,fontSize:12}}>
                {fromB ? (<><span style={{color:"#f59e0b"}}>#{fromB}</span><span style={{color:muted}}> → </span></>) : null}
                <span style={{color:ok?"#10b981":accent,fontWeight:600}}>#{toB}</span>
              </span>
              <span style={{fontSize:11,fontFamily:mono,color:muted,background:card,padding:"2px 8px",borderRadius:5}}>👤 {h.user||"—"}</span>
              <span style={{fontSize:10.5,fontFamily:mono,color:accent,background:`${accent}18`,padding:"2px 8px",borderRadius:5}}>{h.server||""}</span>
              <span style={{fontSize:10.5,fontFamily:mono,color:ok?"#10b981":"#ef4444",fontWeight:600}}>{h.result}</span>
              <span style={{color:muted,fontSize:11,fontFamily:mono,marginLeft:"auto"}}>{(h.time||"").replace("T"," ")}</span>
            </div>
          );})}
        </div>
      </div>

      <style>{`
        .dep-spin{width:15px;height:15px;border:2px solid rgba(148,163,184,0.3);border-top-color:#fff;border-radius:50%;display:inline-block;animation:depSpin .7s linear infinite;}
        @keyframes depSpin{to{transform:rotate(360deg);}}
        @media (max-width: 900px){
          .dep-grid{grid-template-columns:1fr !important;}
          .dep-header{gap:10px !important;}
        }
        @media (max-width: 600px){
          .dep-header > div:last-child{width:100%;}
        }
      `}</style>
    </div>
  );
}
